// Astrology engine core: deterministic tropical Western chart math built on
// astronomy-engine. Nothing in src/lib/astro is ever LLM-generated; the
// interpretation layers read these numbers, never produce them.
//
// Pure functions, no React, no DOM, so they run the same in the browser,
// in node verification scripts, and anywhere else.

import * as Astronomy from "astronomy-engine";

export const mod360 = (x) => ((x % 360) + 360) % 360;
const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

/* ─────────────────────────── signs ─────────────────────────── */

export const SIGNS = [
  { key: "aries",       name: "Aries",       element: "fire",  modality: "cardinal", ruler: "mars",    modernRuler: null },
  { key: "taurus",      name: "Taurus",      element: "earth", modality: "fixed",    ruler: "venus",   modernRuler: null },
  { key: "gemini",      name: "Gemini",      element: "air",   modality: "mutable",  ruler: "mercury", modernRuler: null },
  { key: "cancer",      name: "Cancer",      element: "water", modality: "cardinal", ruler: "moon",    modernRuler: null },
  { key: "leo",         name: "Leo",         element: "fire",  modality: "fixed",    ruler: "sun",     modernRuler: null },
  { key: "virgo",       name: "Virgo",       element: "earth", modality: "mutable",  ruler: "mercury", modernRuler: null },
  { key: "libra",       name: "Libra",       element: "air",   modality: "cardinal", ruler: "venus",   modernRuler: null },
  { key: "scorpio",     name: "Scorpio",     element: "water", modality: "fixed",    ruler: "mars",    modernRuler: "pluto" },
  { key: "sagittarius", name: "Sagittarius", element: "fire",  modality: "mutable",  ruler: "jupiter", modernRuler: null },
  { key: "capricorn",   name: "Capricorn",   element: "earth", modality: "cardinal", ruler: "saturn",  modernRuler: null },
  { key: "aquarius",    name: "Aquarius",    element: "air",   modality: "fixed",    ruler: "saturn",  modernRuler: "uranus" },
  { key: "pisces",      name: "Pisces",      element: "water", modality: "mutable",  ruler: "jupiter", modernRuler: "neptune" },
];

export const signIndexOf = (lon) => Math.floor(mod360(lon) / 30);
export const signOf = (lon) => SIGNS[signIndexOf(lon)];

/** 254.37 → { sign: "Sagittarius", deg: 14, min: 22, text: "14°22′ Sagittarius" } */
export function formatLon(lon) {
  const l = mod360(lon);
  const s = signOf(l);
  let within = l - signIndexOf(l) * 30;
  let deg = Math.floor(within);
  let min = Math.round((within - deg) * 60);
  if (min === 60) { min = 0; deg += 1; }
  return { sign: s.name, deg, min, text: `${deg}°${String(min).padStart(2, "0")}′ ${s.name}` };
}

/* ─────────────────────────── bodies ─────────────────────────── */

export const PLANETS = ["sun", "moon", "mercury", "venus", "mars", "jupiter", "saturn", "uranus", "neptune", "pluto"];
export const PERSONAL = ["sun", "moon", "mercury", "venus", "mars"];
export const SOCIAL = ["jupiter", "saturn"];
export const OUTER = ["uranus", "neptune", "pluto"];

const BODY = {
  sun: Astronomy.Body.Sun,
  moon: Astronomy.Body.Moon,
  mercury: Astronomy.Body.Mercury,
  venus: Astronomy.Body.Venus,
  mars: Astronomy.Body.Mars,
  jupiter: Astronomy.Body.Jupiter,
  saturn: Astronomy.Body.Saturn,
  uranus: Astronomy.Body.Uranus,
  neptune: Astronomy.Body.Neptune,
  pluto: Astronomy.Body.Pluto,
};

const toTime = (date) => (date instanceof Astronomy.AstroTime ? date : Astronomy.MakeTime(date));
const addDays = (date, days) => new Date(date.getTime() + days * 86400000);

/** Apparent geocentric tropical longitude (ecliptic of date), degrees. */
export function bodyLongitude(key, date) {
  const vec = Astronomy.GeoVector(BODY[key], toTime(date), true);
  return mod360(Astronomy.Ecliptic(vec).elon);
}

/** Signed difference b - a folded into (-180, 180]. */
export const signedDelta = (a, b) => {
  let d = mod360(b - a);
  if (d > 180) d -= 360;
  return d;
};

/** Degrees per day, by central difference. Negative = retrograde. */
export function bodySpeed(key, date) {
  const before = bodyLongitude(key, addDays(date, -0.5));
  const after = bodyLongitude(key, addDays(date, 0.5));
  return signedDelta(before, after);
}

/**
 * True (osculating) lunar node: the ascending node of the Moon's
 * instantaneous orbit, from r × v in true-ecliptic-of-date coordinates.
 */
export function trueNodeLongitude(date) {
  const time = toTime(date);
  const state = Astronomy.GeoMoonState(time);
  const rot = Astronomy.Rotation_EQJ_ECT(time);
  const r = Astronomy.RotateVector(rot, new Astronomy.Vector(state.x, state.y, state.z, time));
  const v = Astronomy.RotateVector(rot, new Astronomy.Vector(state.vx, state.vy, state.vz, time));
  const hx = r.y * v.z - r.z * v.y;
  const hy = r.z * v.x - r.x * v.z;
  return mod360(Math.atan2(hx, -hy) * DEG);
}

/** Mean lunar node (Meeus 47.7), kept for comparison and older saved charts. */
export function meanNodeLongitude(date) {
  const T = (date.getTime() / 86400000 - 10957.5) / 36525;
  return mod360(125.0445479 - 1934.1362891 * T + 0.0020754 * T * T + (T * T * T) / 467441);
}

/* ─────────────────────────── time ─────────────────────────── */

/** Offset (minutes east of UTC) that an IANA zone had at a given instant. */
export function zoneOffsetMinutes(timeZone, date) {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone, hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const map = {};
    dtf.formatToParts(date).forEach((p) => { if (p.type !== "literal") map[p.type] = p.value; });
    const asUTC = Date.UTC(+map.year, +map.month - 1, +map.day, +map.hour, +map.minute, +map.second);
    return Math.round((asUTC - date.getTime()) / 60000);
  } catch (e) {
    return 0;
  }
}

/** Local wall-clock components in a zone → the true UTC instant (DST-safe). */
export function localToUtc({ year, month, day, hour = 12, minute = 0 }, timeZone) {
  const wall = Date.UTC(year, month - 1, day, hour, minute, 0);
  // Two passes: the offset at the guessed instant can differ from the offset
  // at the real instant when the birth falls near a DST change.
  let utc = wall - zoneOffsetMinutes(timeZone, new Date(wall)) * 60000;
  utc = wall - zoneOffsetMinutes(timeZone, new Date(utc)) * 60000;
  return new Date(utc);
}

export function to24h(time) {
  if (!time) return 12;
  if (time.period == null) return time.hour;
  let h = time.hour % 12;
  if (time.period === "PM") h += 12;
  return h;
}

/** App birthData shape → UTC Date. Unknown time = local noon. */
export function birthDataToUtc(birthData) {
  const { birthDate, hasBirthTime, birthTime, birthLocation } = birthData;
  return localToUtc(
    {
      year: birthDate.year, month: birthDate.month, day: birthDate.day,
      hour: hasBirthTime ? to24h(birthTime) : 12,
      minute: hasBirthTime && birthTime ? birthTime.minute || 0 : 0,
    },
    birthLocation?.timezone || "UTC"
  );
}

/* ─────────────────────────── angles + houses ─────────────────────────── */

/** Ascendant and Midheaven for an instant and place. */
export function angles(date, lat, lng) {
  const time = toTime(date);
  const ramc = mod360(Astronomy.SiderealTime(time) * 15 + lng); // local apparent sidereal time
  const eps = Astronomy.e_tilt(time).tobl * RAD;
  const t = ramc * RAD;
  const phi = lat * RAD;

  const mc = mod360(Math.atan2(Math.sin(t), Math.cos(t) * Math.cos(eps)) * DEG);
  let asc = mod360(Math.atan2(Math.cos(t), -(Math.sin(t) * Math.cos(eps) + Math.tan(phi) * Math.sin(eps))) * DEG);
  // The Ascendant always sits in the half of the zodiac that follows the MC.
  if (mod360(asc - mc) > 180) asc = mod360(asc + 180);
  return { asc, mc, ramc, eps: eps * DEG };
}

// Ecliptic longitude of the point with a given right ascension.
const lonFromRA = (ra, eps) => mod360(Math.atan2(Math.sin(ra * RAD), Math.cos(ra * RAD) * Math.cos(eps * RAD)) * DEG);
const decOfLon = (lon, eps) => Math.asin(Math.sin(eps * RAD) * Math.sin(lon * RAD)) * DEG;

/**
 * Placidus cusps by iteration on the semi-arcs. Returns null where Placidus
 * is undefined (polar latitudes, where some degrees never rise or set).
 */
export function placidusCusps(ramc, lat, eps, asc, mc) {
  const phi = lat * RAD;
  const cusp = (fraction, above) => {
    // Seed with the equator answer (ascensional difference 0), then iterate.
    let lon = lonFromRA(mod360(above ? ramc + fraction * 90 : ramc + 180 - fraction * 90), eps);
    for (let i = 0; i < 50; i++) {
      const dec = decOfLon(lon, eps) * RAD;
      const x = Math.tan(phi) * Math.tan(dec);
      if (Math.abs(x) >= 1) return null;
      const ad = Math.asin(x) * DEG; // ascensional difference
      const ra = above
        ? ramc + fraction * (90 + ad)          // from MC toward ASC over the diurnal semi-arc
        : ramc + 180 - fraction * (90 - ad);   // from IC toward ASC over the nocturnal semi-arc
      const next = lonFromRA(mod360(ra), eps);
      if (Math.abs(signedDelta(lon, next)) < 1e-7) { lon = next; break; }
      lon = next;
    }
    return lon;
  };
  const c11 = cusp(1 / 3, true);
  const c12 = cusp(2 / 3, true);
  const c2 = cusp(2 / 3, false);
  const c3 = cusp(1 / 3, false);
  if ([c11, c12, c2, c3].some((c) => c == null || Number.isNaN(c))) return null;
  const cusps = [asc, c2, c3, mod360(mc + 180), mod360(c11 + 180), mod360(c12 + 180),
    mod360(asc + 180), mod360(c2 + 180), mod360(c3 + 180), mc, c11, c12];
  // Sanity: cusps must run in zodiacal order around the wheel.
  let total = 0;
  for (let i = 0; i < 12; i++) total += mod360(cusps[(i + 1) % 12] - cusps[i]);
  return Math.abs(total - 360) < 1e-6 ? cusps : null;
}

/** Porphyry: trisect each quadrant. Defined everywhere; the polar fallback. */
export function porphyryCusps(asc, mc) {
  const ic = mod360(mc + 180);
  const q1 = mod360(ic - asc) / 3; // ASC → IC
  const q4 = mod360(asc - mc) / 3; // MC → ASC
  const c2 = mod360(asc + q1), c3 = mod360(asc + 2 * q1);
  const c11 = mod360(mc + q4), c12 = mod360(mc + 2 * q4);
  return [asc, c2, c3, ic, mod360(c11 + 180), mod360(c12 + 180),
    mod360(asc + 180), mod360(c2 + 180), mod360(c3 + 180), mc, c11, c12];
}

export function wholeSignCusps(asc) {
  const first = signIndexOf(asc) * 30;
  return Array.from({ length: 12 }, (_, i) => mod360(first + i * 30));
}

/** Which house (1-12) a longitude falls in, given 12 cusps in order. */
export function houseOf(lon, cusps) {
  for (let i = 0; i < 12; i++) {
    const start = cusps[i];
    const end = cusps[(i + 1) % 12];
    const span = mod360(end - start);
    if (mod360(lon - start) < span) return i + 1;
  }
  return 1;
}

/* ─────────────────────────── dignities ─────────────────────────── */

const DOMICILE = {
  sun: ["leo"], moon: ["cancer"], mercury: ["gemini", "virgo"], venus: ["taurus", "libra"],
  mars: ["aries", "scorpio"], jupiter: ["sagittarius", "pisces"], saturn: ["capricorn", "aquarius"],
  uranus: ["aquarius"], neptune: ["pisces"], pluto: ["scorpio"],
};
const EXALTATION = {
  sun: "aries", moon: "taurus", mercury: "virgo", venus: "pisces",
  mars: "capricorn", jupiter: "cancer", saturn: "libra",
};
const opposite = (signKey) => SIGNS[(SIGNS.findIndex((s) => s.key === signKey) + 6) % 12].key;

/**
 * "domicile" | "exaltation" | "detriment" | "fall" | "detriment+fall" | null.
 * Mercury in Pisces is both in detriment and in fall.
 */
export function dignityOf(planet, signKey) {
  const dom = DOMICILE[planet] || [];
  if (dom.includes(signKey)) return "domicile";
  if (EXALTATION[planet] === signKey) return "exaltation";
  const det = dom.map(opposite).includes(signKey);
  const fall = !!EXALTATION[planet] && opposite(EXALTATION[planet]) === signKey;
  if (det && fall) return "detriment+fall";
  if (det) return "detriment";
  if (fall) return "fall";
  return null;
}

/* ─────────────────────────── aspects ─────────────────────────── */

export const ASPECTS = [
  { key: "conjunction", angle: 0,   orb: 8, major: true },
  { key: "opposition",  angle: 180, orb: 8, major: true },
  { key: "trine",       angle: 120, orb: 7, major: true },
  { key: "square",      angle: 90,  orb: 7, major: true },
  { key: "sextile",     angle: 60,  orb: 5, major: true },
  { key: "quincunx",    angle: 150, orb: 3, major: false },
];

export const separation = (a, b) => {
  const d = mod360(a - b);
  return d > 180 ? 360 - d : d;
};

/**
 * Natal aspects between points. Luminaries get +2° orb, angles and the node
 * are held tighter. `applying` uses each point's daily motion.
 */
export function findAspects(points, { orbScale = 1 } = {}) {
  const keys = Object.keys(points);
  const out = [];
  for (let i = 0; i < keys.length; i++) {
    for (let j = i + 1; j < keys.length; j++) {
      const A = points[keys[i]], Bp = points[keys[j]];
      if (A.lon == null || Bp.lon == null) continue;
      // Angles to each other and node-to-angle contacts are structural, not aspects.
      if (A.kind !== "planet" && Bp.kind !== "planet") continue;
      const lum = [keys[i], keys[j]].some((k) => k === "sun" || k === "moon");
      const tight = [A, Bp].some((p) => p.kind !== "planet");
      const sep = separation(A.lon, Bp.lon);
      for (const asp of ASPECTS) {
        if (tight && !asp.major) continue;
        let orbAllowed = (asp.orb + (lum ? 2 : 0) - (tight ? 2 : 0)) * orbScale;
        if (keys[i] === "northNode" || keys[j] === "northNode") {
          if (asp.key !== "conjunction" && asp.key !== "opposition") continue;
          orbAllowed = 4 * orbScale;
        }
        const orb = Math.abs(sep - asp.angle);
        if (orb <= orbAllowed) {
          const dt = 0.05;
          const later = separation(A.lon + (A.speed || 0) * dt, Bp.lon + (Bp.speed || 0) * dt);
          const entry = {
            a: keys[i], b: keys[j], type: asp.key, major: asp.major,
            orb: +orb.toFixed(2),
            applying: Math.abs(later - asp.angle) < orb,
            // Outer planets move so slowly that their aspects to each other
            // are shared by everyone born within months or years.
            generational: OUTER.includes(keys[i]) && OUTER.includes(keys[j]),
          };
          // Opposite the North Node reads as conjunct the South Node.
          if (asp.key === "opposition" && (keys[i] === "northNode" || keys[j] === "northNode")) {
            const planet = keys[i] === "northNode" ? keys[j] : keys[i];
            Object.assign(entry, { a: planet, b: "southNode", type: "conjunction" });
          }
          out.push(entry);
          break;
        }
      }
    }
  }
  return out.sort((x, y) => x.orb - y.orb);
}

/* ─────────────────────────── moon phase ─────────────────────────── */

// Rudhyar's eight lunation phases, by the Moon's elongation from the Sun.
export const LUNATION_PHASES = [
  { key: "new", name: "New Moon" },
  { key: "crescent", name: "Crescent" },
  { key: "firstQuarter", name: "First Quarter" },
  { key: "gibbous", name: "Gibbous" },
  { key: "full", name: "Full Moon" },
  { key: "disseminating", name: "Disseminating" },
  { key: "lastQuarter", name: "Last Quarter" },
  { key: "balsamic", name: "Balsamic" },
];

// The names everyone else uses for the Moon in the sky. New, First Quarter,
// Full and Last Quarter are moments; the card gives each one about 6 degrees
// either side (roughly half a day), and the shapes between get the rest.
export const QUARTER_WINDOW = 6;
export const MOON_PHASES_COMMON = [
  { key: "new", name: "New Moon", at: 0 },
  { key: "waxingCrescent", name: "Waxing Crescent" },
  { key: "firstQuarter", name: "First Quarter", at: 90 },
  { key: "waxingGibbous", name: "Waxing Gibbous" },
  { key: "full", name: "Full Moon", at: 180 },
  { key: "waningGibbous", name: "Waning Gibbous" },
  { key: "lastQuarter", name: "Last Quarter", at: 270 },
  { key: "waningCrescent", name: "Waning Crescent" },
];

export function commonMoonPhase(elong) {
  const e = mod360(elong);
  for (const p of MOON_PHASES_COMMON) {
    if (p.at == null) continue;
    const d = Math.abs(((e - p.at + 540) % 360) - 180);
    if (d <= QUARTER_WINDOW) return { key: p.key, name: p.name };
  }
  const p = MOON_PHASES_COMMON[2 * Math.floor(e / 90) + 1];
  return { key: p.key, name: p.name };
}

// key/name stay Rudhyar's (birth charts and rituals read them); `common` is
// the sky name the Today card shows.
export function lunationPhase(sunLon, moonLon) {
  const elong = mod360(moonLon - sunLon);
  return {
    ...LUNATION_PHASES[Math.floor(elong / 45) % 8],
    elongation: +elong.toFixed(1), waxing: elong < 180,
    common: commonMoonPhase(elong),
  };
}
